import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { GridComponent, ColumnsDirective, ColumnDirective, Page, Selection, Inject, Edit, Toolbar, Sort, Filter, Search } from '@syncfusion/ej2-react-grids';
import { DetalleInstalacion } from '../components';
import { Tabs, Tab, Select, MenuItem, useMediaQuery } from '@mui/material';
import { L10n } from '@syncfusion/ej2-base';

L10n.load({
  'es-ES': {
    grid: {
      Search: 'Buscar',
      EmptyRecord: 'No hay registros para mostrar',
      Add: 'Agregar',
      Edit: 'Editar',
      Delete: 'Eliminar',
      Update: 'Actualizar',
      Cancel: 'Cancelar',
    },
  },
});

const Instalacion = () => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [selectedTab, setSelectedTab] = useState(0);
  const [allData, setAllData] = useState([]);  // Guarda todos los datos
  const [filteredData, setFilteredData] = useState([]);
  const [detalleInstalacionVisible, setDetalleInstalacionVisible] = useState(false);
  const [selectedInstalacion, setSelectedInstalacion] = useState(null);
  const toolbarOptions = ['Search'];
  // Función para obtener todas las instalaciones
  const fetchInstalaciones = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        console.error('No se ha encontrado el token de autenticación.');
        return;
      } 

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
  
      // Llamada a la API para obtener todas las instalaciones con los detalles ya relacionados
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/instalacion/instalaciones/listar/`, config);
      const instalaciones = response.data;
      // Formatear los datos según las necesidades de tu aplicación
      const instalacionesConDetalles = instalaciones.map((instalacion) => ({
        id_instalacion: instalacion.id_instalacion,
        solicitante: instalacion.solicitante,
        email: instalacion.email || '',
        fecha_instalacion: instalacion.fecha_instalacion,
        tipo_propiedad: instalacion.tipo_propiedad,
        estado_instalacion: instalacion.estado_instalacion,
        direccion_instalacion: instalacion.direccion_instalacion,
        instalacion,  // guarda todo el objeto resumido
      }));
  
      // Guardar todos los datos en el estado
      setAllData(instalacionesConDetalles);
  
      // Aplicar filtro basado en la pestaña seleccionada
      filterData(instalacionesConDetalles, selectedTab);
    } catch (error) {
      console.error('Error al obtener las instalaciones:', error);
    }
  };

  useEffect(() => {
    fetchInstalaciones();  // Llamar a la función para obtener datos al montar el componente
  }, []);

  // Función para filtrar los datos basados en el tabIndex
  const filterData = (data, tabIndex) => {
    let newData = data || allData;  // Usa los datos proporcionados o los ya almacenados
    switch (tabIndex) {
      case 0:
        newData = newData.filter(item => item.estado_instalacion === 'pendiente');
        break;
      case 1:
        newData = newData.filter(item => item.estado_instalacion === 'realizada');
        break;
      case 2:
        newData = newData.filter(item => item.estado_instalacion === 'visita_terreno');
        break;
      default:
        newData = allData;
    }
    setFilteredData(newData);
  };
  const formattedData = useMemo(() => {
    return filteredData.map((item) => ({
      ...item,
      fecha_instalacion: item.fecha_instalacion
        ? new Date(item.fecha_instalacion) // Convertir a Date para ordenamiento correcto
        : null,
      fecha_instalacion_texto: item.fecha_instalacion
        ? new Date(item.fecha_instalacion).toLocaleDateString('es-CL', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          })
        : '',
      hora_instalacion: item.fecha_instalacion
        ? new Date(item.fecha_instalacion).toLocaleTimeString('es-CL', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
          })
        : '',
    }));
  }, [filteredData]);

  const refreshInstalaciones = () => {
    fetchInstalaciones();
  };

  // Llamar a filterData cuando se cambia la pestaña
  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    filterData(allData, newValue);  // Filtrar datos al cambiar de pestaña
  };

  const handleRowClick = async (args) => {
  const rowData = args.data;
  if (!rowData) return;

  try {
    const token = localStorage.getItem('access_token');
    const config = { headers: { Authorization: `Bearer ${token}` } };

    const response = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/api/instalacion/instalacion/${rowData.id_instalacion}/`,
      config
    );

    setSelectedInstalacion(response.data);
    setDetalleInstalacionVisible(true);
  } catch (error) {
    console.error("Error cargando detalle de instalación:", error);
  }
};

  
  const TabWithIcon = ({ color, label }) => (
    <div
      style={{
        display: 'inline-block',
        backgroundColor: color,
        borderRadius: '12px',
        padding: '2px 8px',
        fontSize: '12px', // Tamaño más pequeño
        color: 'white',
        fontWeight: 'bold',
        textAlign: 'center',
        minWidth: '60px',
      }}
    >
      {label}
    </div>
  );

  return (
    <div className="h-full flex flex-col p-2 md:p-4 overflow-hidden">
      <div className="flex-1 min-h-0 flex flex-col bg-white rounded-3xl p-2 md:p-3 overflow-hidden">
      <div className="flex justify-between items-center mb-2 flex-wrap gap-2">
        <p className="text-xl md:text-3xl font-extrabold tracking-tight text-slate-900">Instalaciones</p>
        <div className="flex items-center gap-3">
          {isMobile ? (
            <Select value={selectedTab} onChange={(e) => setSelectedTab(e.target.value)} size="small" sx={{ minWidth: 150 }}>
              <MenuItem value={0}><TabWithIcon color="#FEC90F" label="Pendiente" /></MenuItem>
              <MenuItem value={1}><TabWithIcon color="#8BE78B" label="Realizadas" /></MenuItem>
              <MenuItem value={2}>Visita Terreno</MenuItem>
            </Select>
          ) : (
            <Tabs value={selectedTab} onChange={handleTabChange}
              sx={{
                '.MuiTab-root': { '&:hover': { color: '#FF743A' }, '&.Mui-selected': { color: '#FF743A' } },
                '.MuiTabs-indicator': { backgroundColor: '#FF743A' },
              }}
            >
              <Tab label={<TabWithIcon color="#FEC90F" label="Pendiente" />} />
              <Tab label={<TabWithIcon color="#8BE78B" label="Realizadas" />} />
              <Tab label="Visita Terreno" />
            </Tabs>
          )}
        </div>
      </div>

      {detalleInstalacionVisible && (
        <DetalleInstalacion
          onClose={() => {
            setDetalleInstalacionVisible(false);
            refreshInstalaciones();
          }}
          instalacion={selectedInstalacion}
        />
      )}
      <div className="flex-1 min-h-0 overflow-hidden mt-2">
      <GridComponent
        dataSource={formattedData}
        enableHover={false}
        height="100%"
        allowPaging
        pageSettings={{ pageCount: 5, pageSize: 15 }}
        selectionSettings={{ persistSelection: true, type: 'Single' }}  // Selección de filas individuales
        toolbar={toolbarOptions}
        locale="es-ES"
        editSettings={{ allowEditing: false, allowDeleting: false }}  // Desactivar la edición
        allowSorting
        sortSettings={{
          columns: [{ field: 'fecha_instalacion', direction: 'descending' }]
        }}
        className="mt-4"
        //recordDoubleClick={handleRowDoubleClick}
        rowSelected={handleRowClick}  // Cambiado a 'rowSelected' para clic simple
      >
        <ColumnsDirective>
          <ColumnDirective field="id_instalacion" headerText="ID" width="120" textAlign="Center" />
          <ColumnDirective field="solicitante" headerText="Solicitante" width="200" textAlign="Center" />
          <ColumnDirective 
            field="fecha_instalacion" 
            headerText="Fecha Instalación" 
            width="150" 
            textAlign="Center"
            format="dd/MM/yyyy"
            type="date" // Indica que la columna contiene fechas para ordenar correctamente
            allowSorting={true} 
          />
          <ColumnDirective 
            field="hora_instalacion" 
            headerText="Hora Instalación" 
            width="150" 
            textAlign="Center"
            allowSorting={true} 
          />
          <ColumnDirective field="tipo_propiedad" headerText="Propiedad" width="150" textAlign="Center" />
          <ColumnDirective field="estado_instalacion" headerText="Estado" width="150" textAlign="Center" />
          <ColumnDirective field="direccion_instalacion" headerText="Dirección Instalación" width="200" textAlign="Center" />
        </ColumnsDirective>

        <Inject services={[Page, Selection, Toolbar, Edit, Sort, Filter, Search]} />
      </GridComponent>
      </div>
      </div>
    </div>
  );
};

export default Instalacion;
